import { Controller } from "@hotwired/stimulus"

// Filter chip rows outgrow the toolbar they sit in. Tasks carries six chips,
// Messages seven, and the width they get shrinks with the search field, the
// menus cluster and the assistant panel beside them — none of which the CSS
// breakpoint that swaps chips for the funnel menu can see, because it measures
// the VIEWPORT. So a wide window with the panel open renders a row that doesn't
// fit, and until recently the surplus chips painted straight over the menus.
//
// This gives up the chips that don't fit, rightmost first, into a "More" menu at
// the end of the row. Every chip's rows are already rendered inside that menu
// server-side, one group per chip, so collapsing is only ever a visibility
// decision: no DOM moves, a split chip keeps its own submenu wiring, and with
// JavaScript off the row simply wraps as it did before.
export default class extends Controller {
    static targets = ["overflow"]

    connect() {
        this.remeasure = () => {
            window.cancelAnimationFrame(this.frame)
            this.frame = window.requestAnimationFrame(() => this.measure())
        }
        this.measure()
        // The row connects before its neighbours settle — the assistant panel and
        // the rail mount around it and take width away — so the first reading can
        // be of a row that is about to get narrower. Measure again once the frame
        // is done and once more shortly after, which is cheap and saves the load
        // that would otherwise sit wrapped until the next resize.
        this.settleTimers = [0, 300].map((delay) => window.setTimeout(() => this.measure(), delay))
        // Three triggers, because none covers the others. A window resize is the
        // obvious one; the observer catches the row losing width with the window
        // exactly as wide — the assistant panel opening beside it, a count
        // arriving in a badge — which is the case that produced a wrapped row in
        // the first place.
        this.observer = new ResizeObserver(this.remeasure)
        this.observer.observe(this.element)
        // The row is a flex child: its own width follows whatever the toolbar
        // around it leaves over, so the parent is what actually changes size.
        if (this.element.parentElement) this.observer.observe(this.element.parentElement)
        window.addEventListener("resize", this.remeasure)
        // Chip widths move when the real font lands, and a row measured in the
        // fallback face can be a chip out.
        document.fonts?.ready?.then(this.remeasure)
    }

    disconnect() {
        this.observer?.disconnect()
        window.removeEventListener("resize", this.remeasure)
        window.cancelAnimationFrame(this.frame)
        this.settleTimers?.forEach((timer) => window.clearTimeout(timer))
    }

    get chips() {
        return Array.from(this.element.querySelectorAll(":scope > [data-chip-key]"))
    }

    // The funnel dropdown that stands in for the whole row on a phone. It already
    // holds every filter, flattened, so it is what the row falls back to when
    // there isn't even room for one chip and a "More" button.
    get funnel() {
        return this.element.closest(".network-toolbar")?.querySelector(".network-toolbar-menu--filter-chips")
    }

    measure() {
        if (!this.hasOverflowTarget || this.measuring) return
        // A row handed to the funnel is display:none too, so it could never
        // measure its way back once the room returned (the assistant panel
        // closing, a wider window). Lift the collapse for the reading; the
        // result below puts it back if it still applies.
        const toolbar = this.element.closest(".network-toolbar")
        const wasCollapsed = toolbar?.classList.contains("is-chips-collapsed")
        if (wasCollapsed) toolbar.classList.remove("is-chips-collapsed")
        // Below the chips breakpoint the row is display:none and every width
        // reads 0, which would collapse the lot into a menu nobody can see.
        if (this.element.offsetParent === null) {
            if (wasCollapsed) toolbar.classList.add("is-chips-collapsed")
            return
        }

        this.measuring = true
        try {
            const chips = this.chips
            if (chips.length === 0) return

            // Measure from a clean slate — a hidden chip has no width to add up.
            chips.forEach((chip) => { chip.hidden = false })
            this.overflowTarget.hidden = true

            const available = this.element.clientWidth
            const gap = parseFloat(getComputedStyle(this.element).columnGap) || 8
            const widths = new Map(chips.map((chip) => [chip, chip.offsetWidth]))
            const spaceFor = (list) => list.reduce((sum, chip) => sum + widths.get(chip), 0) + gap * Math.max(list.length - 1, 0)

            if (spaceFor(chips) <= available) {
                this.collapseRow(false)
                this.apply([])
                return
            }

            this.overflowTarget.hidden = false
            const buttonSpace = this.overflowTarget.offsetWidth + gap

            // Rightmost chips go first, and inactive ones before active: a filter
            // that is ON has to stay on screen, or the row stops describing what
            // you are looking at. Among the active ones it is left to right, so
            // the leader ("All", which is active whenever nothing else is) is
            // given up before the filter actually doing the narrowing.
            const rightToLeft = [...chips].reverse()
            const order = rightToLeft.filter((chip) => !this.isActive(chip))
                .concat(chips.filter((chip) => this.isActive(chip)))
            const hidden = []
            let visible = chips
            while (order.length && visible.length > 1 && spaceFor(visible) + buttonSpace > available) {
                const next = order.shift()
                hidden.push(next)
                visible = visible.filter((chip) => chip !== next)
            }

            // One chip and a button still over the line: the row has no useful
            // form left, so hand the whole thing to the funnel rather than show a
            // single arbitrary chip beside a menu holding the other five.
            if (spaceFor(visible) + buttonSpace > available) {
                this.collapseRow(true)
                return
            }

            this.collapseRow(false)
            this.apply(hidden)
        } finally {
            this.measuring = false
        }
    }

    isActive(chip) {
        return chip.classList.contains("is-active")
    }

    // The whole row for the funnel. Chips are put back on show first: the row is
    // display:none either way, and it has to measure honestly when it returns.
    collapseRow(collapsed) {
        const toolbar = this.element.closest(".network-toolbar")
        if (collapsed) {
            this.chips.forEach((chip) => { chip.hidden = false })
            this.overflowTarget.hidden = true
        }
        toolbar?.classList.toggle("is-chips-collapsed", collapsed)
        this.funnel?.classList.toggle("network-toolbar-menu--standing", collapsed)
    }

    apply(hidden) {
        const keys = new Set(hidden.map((chip) => chip.dataset.chipKey))
        this.chips.forEach((chip) => { chip.hidden = keys.has(chip.dataset.chipKey) })
        this.overflowTarget.hidden = keys.size === 0

        // Groups carry a rule between them, so the first VISIBLE one has to lose
        // its own — otherwise the menu opens on a stray line.
        let first = true
        this.overflowTarget.querySelectorAll("[data-chip-group]").forEach((group) => {
            const show = keys.has(group.dataset.chipGroup)
            group.hidden = !show
            group.classList.toggle("is-first", show && first)
            if (show) first = false
        })
    }
}
